import { Component, input } from '@angular/core';

import { Button, ButtonIcon } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';

import { U2bDatePickerApplyDirective } from '../directives/u2b-date-picker-apply.directive';

type TimeUnit = 'hours' | 'minutes';

/** Keys that must reach the panel's own handler (Escape closes it, Tab is trapped). */
const PANEL_KEYS = ['Escape', 'Tab'];

/**
 * Typable hour/minute for a `<p-datepicker [showTime]>`, replacing PrimeNG's spinner —
 * that one renders both values as read-only `<span>`s, so the time can only be dialled
 * with the arrows and never selected and overwritten.
 *
 * Rendered by `U2bDatePickerButtonBarComponent` in the `#buttonbar` slot, right below
 * where the stock spinner sits; that one is hidden per picker via
 * {@link U2B_DATE_PICKER_CUSTOM_TIME_PT}.
 *
 * Change detection is left on `Default`: the values come from the picker's own
 * `currentHour`/`currentMinute` fields rather than from inputs, so the view has to be
 * re-checked whenever the panel is. Binding `[value]` keeps a half-typed field intact
 * (an unchanged binding is not written back to the DOM) — which is why `commit()`
 * restores the field itself when the typed text was not usable.
 */
@Component({
    selector: 'u2b-date-picker-time-input',
    standalone: true,
    imports: [Button, ButtonIcon, TranslationModule],
    template: `
        <div class="time">
            @for (unit of units; track unit) {
                @if (!$first) {
                    <span class="time__separator" aria-hidden="true">:</span>
                }

                <div class="time__unit">
                    <p-button
                        type="button"
                        size="small"
                        rounded
                        variant="text"
                        severity="secondary"
                        [ariaLabel]="('common.datePicker.' + unit + 'Increment' | translate)"
                        (keydown)="picker().onButtonKeydown($event)"
                        (onClick)="picker().stepTime(unit, 1, $event)">
                        <span pButtonIcon class="material-icons">expand_less</span>
                    </p-button>

                    <input
                        class="time__field"
                        type="text"
                        inputmode="numeric"
                        autocomplete="off"
                        maxlength="2"
                        [attr.aria-label]="('common.datePicker.' + unit | translate)"
                        [value]="text(unit)"
                        (input)="stripNonDigits($event)"
                        (keydown)="onFieldKeydown($event, unit)"
                        (blur)="commit($event, unit)"/>

                    <p-button
                        type="button"
                        size="small"
                        rounded
                        variant="text"
                        severity="secondary"
                        [ariaLabel]="('common.datePicker.' + unit + 'Decrement' | translate)"
                        (keydown)="picker().onButtonKeydown($event)"
                        (onClick)="picker().stepTime(unit, -1, $event)">
                        <span pButtonIcon class="material-icons">expand_more</span>
                    </p-button>
                </div>
            }
        </div>
    `,
    styles: [`
        .time {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.25rem;
        }

        .time__unit {
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .time__separator {
            font-size: 1.125rem;
            color: var(--p-datepicker-time-picker-color, var(--p-text-color));
        }

        .time__field {
            width: 2.75rem;
            padding: 0.125rem 0;
            border: 1px solid var(--p-inputtext-border-color);
            border-radius: var(--p-inputtext-border-radius);
            background: var(--p-inputtext-background);
            color: var(--p-inputtext-color);
            font-family: inherit;
            font-size: 1.125rem;
            text-align: center;
        }

        .time__field:focus {
            outline: 0;
            border-color: var(--p-inputtext-focus-border-color);
            box-shadow: var(--p-inputtext-focus-ring-shadow);
        }
    `],
})
export class U2bDatePickerTimeInputComponent {

    readonly picker = input.required<U2bDatePickerApplyDirective>();

    readonly units: TimeUnit[] = ['hours', 'minutes'];

    text(unit: TimeUnit): string {
        return `${this.picker().panelTime()[unit]}`.padStart(2, '0');
    }

    onFieldKeydown(event: KeyboardEvent, unit: TimeUnit): void {
        if (PANEL_KEYS.includes(event.key)) {
            this.picker().onButtonKeydown(event);

            return;
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            this.picker().stepTime(unit, event.key === 'ArrowUp' ? 1 : -1, event);

            return;
        }

        // Enter confirms the whole panel, matching the date field and "Übernehmen".
        if (event.key === 'Enter') {
            this.commit(event, unit);
            this.picker().close();
        }
    }

    /**
     * Writes the typed value through, then rewrites the field from the picker — an entry the
     * picker refused (unparseable, or pulled back by min/max) would otherwise stay on screen,
     * because `[value]` skips the DOM when the bound value has not changed.
     */
    commit(event: Event, unit: TimeUnit): void {
        const field = event.target as HTMLInputElement;
        const typed = parseInt(field.value, 10);

        if (!isNaN(typed)) {
            this.picker().setPanelTime({ ...this.picker().panelTime(), [unit]: typed });
        }

        field.value = this.text(unit);
    }

    stripNonDigits(event: Event): void {
        const field = event.target as HTMLInputElement;
        const digits = field.value.replace(/\D/g, '');

        if (digits !== field.value) {
            field.value = digits;
        }
    }
}
